import React, { useState, useEffect } from 'react';
import { Save, ListChecks } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import FormModalLayout from '../Common/FormModalLayout';
import { ensureRichTextValue, RichTextEditor } from '../RichTextEditor';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const CompanyStatusModal = ({ companyStatus, onClose, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  const isEditing = !!companyStatus;

  useEffect(() => {
    if (companyStatus) {
      setValue('code', companyStatus.code);
      setValue('name', companyStatus.name);
      setValue('description', ensureRichTextValue(companyStatus.description || ''));
      setValue('isActiveStatus', companyStatus.isActiveStatus === true || companyStatus.isActiveStatus === 'true' ? 'true' : 'false');
      setValue('isActive', companyStatus.isActive === true || companyStatus.isActive === 'true' ? 'true' : 'false');
    } else {
      reset({
        code: '',
        name: '',
        description: '',
        isActiveStatus: 'true',
        isActive: 'true',
      });
    }
  }, [companyStatus, setValue, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving company status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormModalLayout
      title={isEditing ? 'Editar status de empresa' : 'Novo status de empresa'}
      subtitle="Código curto e regras de uso no cadastro."
      headerIcon={ListChecks}
      onClose={onClose}
      size="lg"
      historyEntityType="company_statuses"
      historyEntityId={companyStatus?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isLoading}>
            Cancelar
          </button>
          <Can permission={P.CATALOG_ACCESS}>
            <button
              type="submit"
              form="company-status-modal-form"
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : (
                <Save className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isEditing ? 'Atualizar' : 'Salvar'} status
            </button>
          </Can>
        </>
      }
    >
      <form id="company-status-modal-form" onSubmit={handleSubmit(onSubmit)} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="form-label form-label-required">Código</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: AT"
              maxLength={2}
              {...register('code', {
                required: 'Código é obrigatório',
                maxLength: { value: 2, message: 'Código deve ter no máximo 2 caracteres' },
              })}
            />
            {errors.code && <p className="form-error">{errors.code.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Nome</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: Ativa"
              {...register('name', { required: 'Nome é obrigatório' })}
            />
            {errors.name && <p className="form-error">{errors.name.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Status ativo</label>
            <select className="form-select" {...register('isActiveStatus')}>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Ativo no sistema</label>
            <select className="form-select" {...register('isActive')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                variant="compact"
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Descrição do status de empresa..."
              />
            )}
          />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default CompanyStatusModal;
